import { styled } from '@mui/material/styles';
import {
  Chip,
  Collapse,
  Drawer as MuiDrawer,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { rgba } from 'polished';
import * as React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { NavLink as RouterNavLink } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { observer } from 'mobx-react-lite';
import { Theme } from '@mui/material/styles';

import { MaterialsGroup, Materials } from 'app/models/CustomerModels';
import { useStores } from 'app/stores/config/UseStores';
import OstroLogo from 'assets/images/ostro-logo.png';
import { AccessLevel } from 'app/models/RouteModels';
import analytics from 'app/services/analytics';

import MaterialsDialog from './MaterialsDialog';

const Scrollbar = styled(PerfectScrollbar)`
  border-right: 0;
  background-color: #222;
`;

const List = styled(MuiList)`
  background-color: #222;
`;

const Items = styled('div')`
  padding-top: ${(props) => props.theme.spacing(2.5)};
  padding-bottom: ${(props) => props.theme.spacing(2.5)};
`;

const Brand = styled(MuiListItem)`
  font-size: ${(props) => props.theme.typography.h4.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.palette.common.white};
  background-color: #222;
  font-family: ${(props) => props.theme.typography.fontFamily};
  height: 56px;

  svg {
    margin: 0 auto;
  }

  ${(props) => props.theme.breakpoints.up('sm')} {
    height: 64px;
  }
`;

const Category = styled(MuiListItem)`
  padding-top: ${(props) => props.theme.spacing(3)};
  padding-bottom: ${(props) => props.theme.spacing(3)};
  cursor: pointer;

  svg {
    color: ${(props) => props.theme.palette.common.white};
    font-size: 20px;
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.12);
  }

  &.active {
    background-color: #333;

    span {
      color: ${(props) => props.theme.palette.grey['100']};
    }
  }
`;

const CategoryText = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) => props.theme.palette.common.white};
    font-size: ${(props) => props.theme.typography.body1.fontSize};
    padding: 0 16px;
  }
`;

const CategoryIconLess = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.palette.common.white, 0.5)};
`;

const CategoryIconMore = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.palette.common.white, 0.5)};
`;

const Link = styled(MuiListItem)<{ component: typeof RouterNavLink; to: string }>`
  padding-left: ${(props) => props.theme.spacing(12)};
  padding-top: ${(props) => props.theme.spacing(2)};
  padding-bottom: ${(props) => props.theme.spacing(2)};

  span {
    color: ${(props) => rgba(props.theme.palette.common.white, 0.7)};
  }

  &:hover span {
    color: ${(props) => rgba(props.theme.palette.common.white, 0.9)};
  }
`;

const LinkText = styled(ListItemText)`
  color: ${(props) => props.theme.palette.common.white};
  span {
    font-size: ${(props) => props.theme.typography.body1.fontSize};
  }
  margin-top: 0;
  margin-bottom: 0;
`;

const SidebarHeader = styled(Typography)`
  color: ${(props) => props.theme.palette.common.white};
  padding: ${(props) => props.theme.spacing(2)} ${(props) => props.theme.spacing(4)}px
    ${(props) => props.theme.spacing(1)};
  opacity: 0.9;
  display: block;
`;

const SidebarBottom = styled('div')({
  backgroundColor: '#222',
});

const CategoryLink = styled(MuiListItem)`
  padding-top: ${(props) => props.theme.spacing(1)};
  padding-bottom: ${(props) => props.theme.spacing(1)};
  cursor: pointer;
  color: #ffffff;

  &:hover {
    color: #6918d0;
  }

  svg {
    font-size: 20px;
    padding-right: 8px;
  }
`;

const CategoryTextLink = styled(ListItemText)`
  margin: 0;
  span {
    color: #ffffff;

    font-size: ${(props) => props.theme.typography.body1.fontSize};
    text-decoration: underline;
    text-wrap: auto;

    &:hover {
      color: #6918d0;
    }
  }
`;

const StyledChip = styled(Chip)`
  background-color: #ffffff;
  span {
    color: #000 !important;
  },
}
`;

type SidebarCategoryProps = {
  name: string;
  icon: React.ReactNode;
  isOpen: boolean;
  isCollapsable: boolean;
  activeClassName: string;
  counter: React.ReactNode;
  open: boolean;
  button?: boolean;
  onClick?: () => void;
  theme?: unknown;
  to?: unknown;
  component?: unknown;
  exact?: boolean;
};

export const SidebarCategory = ({
  name,
  icon,
  isOpen,
  isCollapsable,
  activeClassName,
  counter,
  open,
  ...rest
}: SidebarCategoryProps) => {
  return (
    <Category
      {...(rest as Omit<
        SidebarCategoryProps,
        'theme' | 'name' | 'icon' | 'isOpen' | 'isCollapsable' | 'activeClassName' | 'counter' | 'open'
      >)}
    >
      {icon}
      {open && <CategoryText>{name}</CategoryText>}
      {open && counter && (
        <StyledChip
          data-testid="counter-chip"
          label={counter}
          size="small"
        />
      )}
      {open && isCollapsable ? isOpen ? <CategoryIconMore /> : <CategoryIconLess /> : null}
    </Category>
  );
};

type SidebarLinkProps = {
  name: React.ReactNode;
  to: string; // Change unknown to string
  open: boolean;
};

const SidebarLink = ({ name, to, open }: SidebarLinkProps) => {
  return (
    <Link
      component={RouterNavLink}
      dense
      to={to}
      sx={{
        justifyContent: open ? 'flex-start' : 'center',
        px: open ? 3 : 2,
      }}
    >
      {open && <LinkText>{name}</LinkText>}
    </Link>
  );
};

interface IProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routes: any;
  open?: boolean;
  onClose?: () => void;
}

const DRAWER_WIDTH = 248;
const COLLAPSED_DRAWER_WIDTH = 54;

const Drawer = styled(MuiDrawer)<{ theme?: Theme; open: boolean }>(({ theme, open }) => ({
  width: open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.primary.main,
    width: open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  },
}));

const Sidebar = ({ routes, onClose }: IProps) => {
  const {
    uiStores: { layoutStore },
    dataStores: { authStore },
  } = useStores();

  const [openModal, setOpenModal] = React.useState(false);
  const [materials, setMaterials] = React.useState<Materials>();
  const [selectedMaterials, setSelectedMaterials] = React.useState<MaterialsGroup | null>(null);

  const role = AccessLevel.Public;

  const toggle = (index: number) => {
    layoutStore.toggleRoute(index);
  };

  React.useEffect(() => {
    if (authStore.isAuth && authStore.isConcierge) {
      (async () => {
        const res = await layoutStore.loadMaterials();
        if (typeof res !== 'boolean') setMaterials(res);
      })();
    }
  }, [authStore.authData?.access, layoutStore]);

  React.useEffect(() => {
    if (routes) {
      const pathName = location.pathname;
      routes.forEach((route, index) => {
        layoutStore.setRouteState(index, pathName.startsWith(route.path));
      });
    }
  }, [routes, layoutStore]);

  return (
    <>
      <Drawer
        variant="permanent"
        open={layoutStore.sidebarOpen}
        onClose={onClose}
        PaperProps={{
          style: {
            width: layoutStore.sidebarOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            border: 'none',
            position: 'fixed',
          },
        }}
        sx={{
          width: layoutStore.sidebarOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: layoutStore.sidebarOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Scrollbar>
          <List disablePadding>
            {layoutStore.sidebarOpen && (
              <Brand>
                <img
                  width="162"
                  height="41"
                  src={OstroLogo}
                  alt="Ostro Logo"
                />
              </Brand>
            )}
            <Items>
              {routes?.map((category, index) => (
                <React.Fragment key={index}>
                  {category.header && <SidebarHeader variant="caption">{category.header}</SidebarHeader>}

                  {category.children ? (
                    <React.Fragment key={index}>
                      <SidebarCategory
                        isOpen={!layoutStore.routesState[index]}
                        isCollapsable={true}
                        name={category.id}
                        icon={category.icon}
                        activeClassName="active"
                        button={true}
                        onClick={() => toggle(index)}
                        counter={null}
                        open={layoutStore.sidebarOpen}
                      />

                      <Collapse
                        in={layoutStore.routesState[index]}
                        timeout="auto"
                        unmountOnExit
                      >
                        {category.children.map((route, idx) => (
                          <SidebarLink
                            key={idx}
                            name={route.name}
                            to={route.path}
                            open={layoutStore.sidebarOpen}
                          />
                        ))}
                      </Collapse>
                    </React.Fragment>
                  ) : category.access.includes(role) ||
                    (category.access.length === 1 && category.access.includes(AccessLevel.Auth)) ||
                    (category.access.includes(AccessLevel.Auth) &&
                      category.access.includes(authStore.authData?.profile_type)) ? (
                    <SidebarCategory
                      isOpen={false}
                      isCollapsable={false}
                      name={category.id}
                      to={category.path}
                      activeClassName="active"
                      component={RouterNavLink}
                      icon={category.icon}
                      onClick={onClose}
                      counter={category?.counter}
                      exact
                      open={layoutStore.sidebarOpen}
                    />
                  ) : null}
                </React.Fragment>
              ))}
            </Items>
          </List>
        </Scrollbar>
        {layoutStore.sidebarOpen && (
          <SidebarBottom>
            <Scrollbar>
              <Items>
                {materials?.groups?.map((item, index) => (
                  // TODO: fix types
                  // @ts-ignore
                  <CategoryLink
                    key={index}
                    onClick={() => {
                      analytics.captureQuickLinkGroupClicked(item);
                      setSelectedMaterials(item);
                      setOpenModal(true);
                    }}
                  >
                    <CategoryTextLink>{item.group_name}</CategoryTextLink>
                  </CategoryLink>
                ))}

                {materials?.general?.map((item, index) => (
                  // TODO: fix types
                  // @ts-ignore
                  <CategoryLink
                    key={index + 100}
                    onClick={() => {
                      analytics.captureQuickLinkClicked(item);
                      if (item.link || item.file) {
                        // @ts-ignore
                        window.open(item.link || item.file, '_blank').focus();
                      }
                    }}
                  >
                    <OpenInNewIcon />
                    <CategoryTextLink>{item.name}</CategoryTextLink>
                  </CategoryLink>
                ))}
              </Items>
            </Scrollbar>
          </SidebarBottom>
        )}
      </Drawer>

      <MaterialsDialog
        open={openModal}
        materials={selectedMaterials!}
        onClose={() => {
          setSelectedMaterials(null);
          setOpenModal(false);
        }}
      />
    </>
  );
};

export default observer(Sidebar);
